import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PAYMENT_EMAIL } from "@/lib/brand";
import { Mail, Package, Truck, AlertCircle, CheckCircle2, Clock } from "lucide-react";

const STATUS_META = {
  new: { label: "Awaiting review", color: "text-blue-300 bg-blue-500/10 border-blue-500/40", icon: Clock,
    note: "We've got your request. We'll send your final total here as soon as we review it." },
  contacted: { label: "Total ready — send payment", color: "text-yellow-300 bg-yellow-400/10 border-yellow-400/40", icon: AlertCircle,
    note: "Your total is below. Send Etransfer or PayPal to the address below to lock in your spot." },
  queued: { label: "Payment received — order being prepared", color: "text-purple-300 bg-purple-500/10 border-purple-500/40", icon: Package,
    note: "Payment received. We're packing your order and will update you with tracking as soon as it ships." },
  completed: { label: "Completed", color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40", icon: CheckCircle2,
    note: "Thanks for trading with us!" },
  cancelled: { label: "Cancelled", color: "text-red-300 bg-red-500/10 border-red-500/40", icon: AlertCircle,
    note: "This order was cancelled. Reach out if that's a mistake." },
};

export default function OrderStatusPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) {
      setError("Missing order ID.");
      setLoading(false);
      return;
    }
    api.get(`/orders/lookup/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || "Order not found"))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) {
    return <div className="text-center py-32 text-slate-400" data-testid="order-status-loading">Looking up your order…</div>;
  }
  if (error) {
    return (
      <div data-testid="order-status-error" className="max-w-md mx-auto px-4 py-24 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
        <h1 className="mt-4 font-[Outfit] text-2xl font-black">Couldn&apos;t find that order.</h1>
        <p className="mt-2 text-slate-400 text-sm">{error}</p>
        <Link to="/checkout" className="mt-6 inline-block text-yellow-400 hover:text-yellow-300 font-mono text-sm uppercase">Place a new order →</Link>
      </div>
    );
  }

  const meta = STATUS_META[order.status] || STATUS_META.new;
  const Icon = meta.icon;
  const items = (order.items && order.items.length > 0)
    ? order.items
    : [{ product_name: order.product_name, quantity: order.quantity, unit_price: order.unit_price }];
  const itemsTotal = items.reduce((sum, it) => sum + (it.unit_price || 0) * (it.quantity || 1), 0);
  const hasPricing = itemsTotal > 0 || order.shipping_cost > 0 || order.total > 0;
  const headerTitle = items.length === 1
    ? items[0].product_name
    : `${items.length} items`;

  return (
    <div data-testid="order-status-page" className="max-w-2xl mx-auto px-4 sm:px-8 py-16">
      <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-3">// Your order</div>
      <h1 className="font-[Outfit] text-4xl sm:text-5xl font-black tracking-tighter">
        {headerTitle}
      </h1>
      <p className="mt-2 text-slate-400 text-sm">
        Hi {order.customer_name} — here&apos;s the latest on your request.
      </p>

      <div className={`mt-6 flex items-start gap-3 border rounded-xl p-4 ${meta.color}`}>
        <Icon className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <div className="font-bold uppercase tracking-wider text-xs font-mono">{meta.label}</div>
          <div className="text-sm text-slate-200 mt-1 leading-relaxed">{meta.note}</div>
        </div>
      </div>

      {/* Totals */}
      <div className="mt-6 bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-4">Cost breakdown</h2>
        <div className="space-y-2 text-sm" data-testid="order-items-list">
          {items.map((it, i) => {
            const line = (it.unit_price || 0) * (it.quantity || 1);
            return (
              <Row
                key={i}
                label={`${it.product_name} × ${it.quantity || 1}`}
                value={line > 0 ? `$${line.toFixed(2)}` : "Pending quote"}
              />
            );
          })}
          <Row label="Shipping" value={order.needs_shipping ? (hasPricing ? `$${order.shipping_cost.toFixed(2)}` : "To be quoted") : "Not required"} icon={<Truck className="w-3.5 h-3.5 text-slate-500 inline mr-1" />} />
        </div>
        <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-400">Total</span>
          <span data-testid="order-total" className="text-3xl font-mono font-black text-yellow-400 tabular-nums">
            {hasPricing ? `$${order.total.toFixed(2)}` : "—"}
          </span>
        </div>
        <p className="mt-2 text-[10px] text-slate-500 font-mono uppercase tracking-wider">CAD</p>
      </div>

      {/* Payment instructions — only when admin has set the total */}
      {hasPricing && order.status !== "completed" && order.status !== "cancelled" && (
        <div className="mt-4 bg-slate-900 border border-yellow-400/30 rounded-2xl p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-yellow-400 mb-2">
            How to pay
          </div>
          <p className="text-sm text-slate-300 leading-relaxed">
            Send <span className="text-yellow-400 font-bold">${order.total.toFixed(2)} CAD</span>{" "}
            via{" "}
            <span className="text-white font-bold">
              {order.payment_method === "etransfer" ? "Interac Etransfer" : "PayPal"}
            </span>{" "}
            to:
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <code className="bg-slate-950 border border-slate-800 px-3 py-2 rounded text-yellow-400 font-mono text-sm break-all">
              {PAYMENT_EMAIL}
            </code>
            <button
              type="button"
              data-testid="order-status-copy-email"
              onClick={() => navigator.clipboard?.writeText(PAYMENT_EMAIL)}
              className="bg-yellow-400 text-slate-950 font-bold px-3 py-2 rounded-md text-xs uppercase tracking-wide hover:bg-yellow-300"
            >
              <Mail className="w-3 h-3 inline mr-1" /> Copy email
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Add a memo with your name &amp; order ID so we can match the payment.
          </p>
        </div>
      )}

      {/* Order meta */}
      <div className="mt-6 bg-slate-900 border border-slate-800 rounded-2xl p-6 text-sm space-y-2">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-3">Details</div>
        <Detail
          label="Delivery"
          value={
            order.shipping_method === "pickup"
              ? "Local pickup (free)"
              : `Tracked shipping — $${(order.shipping_cost || 0).toFixed(2)}`
          }
        />
        {order.shipping_method === "ship_flat" && order.shipping_address && (
          <Detail label="Shipping to" value={order.shipping_address} />
        )}
        <Detail label="Email" value={order.email} />
        {order.phone && <Detail label="Phone" value={order.phone} />}
        {order.message && <Detail label="Notes" value={order.message} />}
        <Detail label="Submitted" value={order.created_at.slice(0, 19).replace("T", " ") + " UTC"} mono />
        <Detail label="Order ID" value={order.id} mono small />
      </div>

      <p className="mt-6 text-xs text-center text-slate-500">
        Bookmark this page to come back any time. Questions? Email{" "}
        <a href={`mailto:${PAYMENT_EMAIL}`} className="text-yellow-400 hover:text-yellow-300">{PAYMENT_EMAIL}</a>{" "}
        or DM <a href="https://www.tiktok.com/@washeduptrainerss" target="_blank" rel="noreferrer" className="text-yellow-400 hover:text-yellow-300">@washeduptrainerss</a>.
      </p>
    </div>
  );
}

function Row({ label, value, icon }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400">{icon}{label}</span>
      <span className="text-slate-200 tabular-nums font-mono">{value}</span>
    </div>
  );
}

function Detail({ label, value, mono, small }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-slate-500 text-xs font-mono uppercase tracking-wider shrink-0">{label}</span>
      <span className={`text-slate-300 text-right ${mono ? "font-mono" : ""} ${small ? "text-[11px] break-all" : ""}`}>{value}</span>
    </div>
  );
}
